import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, DollarSign, TrendingUp, GraduationCap, Bookmark, BookmarkCheck,
  Star, Trophy, RefreshCw, Users, Share2, Download, Briefcase, Check, BadgeCheck,
  UserRound, Send, Phone, X, Award, Sun,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ScoreRing({ score }) {
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <path d="M18 2 a16 16 0 0 1 0 32 a16 16 0 0 1 0 -32" fill="none" stroke="#000" strokeWidth="4" opacity="0.15" />
        <motion.path
          d="M18 2 a16 16 0 0 1 0 32 a16 16 0 0 1 0 -32"
          fill="none" stroke="#FF6B6B" strokeWidth="4" strokeLinecap="round"
          strokeDasharray="100"
          initial={{ strokeDashoffset: 100 }}
          animate={{ strokeDashoffset: 100 - score }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading text-lg font-bold">{score}%</span>
    </div>
  );
}

export default function Results() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(new Set());
  const [career, setCareer] = useState({ profession: "", happiness: 0 });
  const [careerSaved, setCareerSaved] = useState(false);
  const [showRecruiter, setShowRecruiter] = useState(false);
  const [recruiter, setRecruiter] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const openRecruiter = () => {
    setRecruiter((r) => ({
      ...r,
      name: r.name || user?.name || "",
      email: r.email || user?.email || "",
    }));
    setShowRecruiter(true);
  };

  const submitRecruiter = async () => {
    if (!recruiter.name.trim() || !recruiter.email.trim() || !recruiter.phone.trim()) {
      toast("Please add your name, email and phone.");
      return;
    }
    setSending(true);
    try {
      await api.post("/recruiter/connect", recruiter);
      toast.success("Sent! A recruiter will reach out soon.");
      setShowRecruiter(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const load = useCallback(async () => {
    const [res, sav, prof] = await Promise.all([
      api.get("/assessment/latest"),
      api.get("/professions/saved"),
      api.get("/profile/career"),
    ]);
    setData(res.data);
    setSaved(new Set(sav.data.saved.map((s) => s.onet_code)));
    if (prof.data.profession) {
      setCareer({ profession: prof.data.profession, happiness: prof.data.happiness || 0 });
      setCareerSaved(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const share = async () => {
    if (!data?.assessment_id) return;
    const url = `${window.location.origin}/share/${data.assessment_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch {
      toast(url);
    }
  };

  const downloadPdf = () => {
    if (!data?.assessment_id) return;
    const a = document.createElement("a");
    a.href = `${BACKEND}/api/public/results/${data.assessment_id}/pdf`;
    a.download = "career-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const saveCareer = async () => {
    if (!career.profession.trim() || !career.happiness) {
      toast("Add your profession and a happiness rating first.");
      return;
    }
    await api.post("/profile/career", { profession: career.profession.trim(), happiness: career.happiness });
    setCareerSaved(true);
    toast.success("Thanks! You're now part of the community insights.");
    load();
  };

  const toggleSave = async (occ) => {
    if (saved.has(occ.onet_code)) {
      await api.delete(`/professions/saved/${occ.onet_code}`);
      setSaved((s) => { const n = new Set(s); n.delete(occ.onet_code); return n; });
      toast("Removed from saved");
    } else {
      await api.post("/professions/save", {
        onet_code: occ.onet_code, title: occ.title, match_score: occ.match_score,
        salary: occ.salary, outlook: occ.outlook,
      });
      setSaved((s) => new Set(s).add(occ.onet_code));
      toast.success("Saved to your list!");
    }
  };

  if (!data) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-brand-primary" /></div>;
  }

  if (!data.matches.length) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <div className="nb-card p-8 bg-brand-yellow">
          <h2 className="font-heading text-2xl font-bold mb-3">No matches yet</h2>
          <p className="font-semibold mb-6">Take the swipe assessment to discover your careers.</p>
          <button data-testid="start-assessment-btn" onClick={() => nav("/assessment")} className="nb-btn bg-brand-primary text-white">Start Swiping</button>
        </div>
      </div>
    );
  }

  const [top, ...rest] = data.matches;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="nb-badge bg-brand-yellow mb-2"><Trophy className="w-4 h-4" /> Your Career Matches</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-black">Careers that fit you</h1>
        </div>
        <button data-testid="retake-btn" onClick={() => nav("/assessment")} className="nb-btn bg-white hidden sm:flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Retake
        </button>
      </div>

      {/* Share / export bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="share-btn" onClick={share} className="nb-btn bg-brand-secondary flex items-center gap-2">
          <Share2 className="w-4 h-4" /> Share results
        </button>
        <button data-testid="download-pdf-btn" onClick={downloadPdf} className="nb-btn bg-brand-yellow flex items-center gap-2">
          <Download className="w-4 h-4" /> Download PDF
        </button>
        <button data-testid="connect-recruiter-btn" onClick={openRecruiter} className="nb-btn bg-brand-primary text-white flex items-center gap-2">
          <UserRound className="w-4 h-4" /> Connect with a Recruiter
        </button>
      </div>

      {data.veteran && (data.veteran.code || data.veteran.rating_title) && (
        <motion.div
          data-testid="veteran-thankyou"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="nb-card p-4 bg-brand-secondary mb-6 flex items-center gap-3"
        >
          <div className="bg-white border-2 border-black rounded-lg p-2 shadow-nb-xs">
            <Award className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold text-black leading-tight">Thank you for your service. 🇺🇸</p>
            <p className="text-sm font-semibold text-black/70">
              Matched from your military background: {[data.veteran.branch, data.veteran.code, data.veteran.rating_title].filter(Boolean).join(" · ")}
            </p>
          </div>
        </motion.div>
      )}

      {/* Top match hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        data-testid="top-match-card"
        className="nb-card p-6 md:p-8 bg-brand-secondary mb-6"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <ScoreRing score={top.match_score} />
          <div className="flex-1">
            <span className="nb-badge bg-brand-yellow mb-2">Top Match</span>
            {top.interest_area && (
              <span data-testid="top-interest-tag" className="nb-badge ml-2 mb-2" style={{ backgroundColor: top.interest_area.color }}>
                Mostly {top.interest_area.title}
              </span>
            )}
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-black">{top.title}</h2>
            <p className="font-semibold text-black/80 mt-2 max-w-2xl">{top.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="nb-badge bg-white"><DollarSign className="w-4 h-4" /> ${top.salary.toLocaleString()}/yr</span>
              <span className="nb-badge bg-white"><TrendingUp className="w-4 h-4" /> {top.outlook}</span>
              <span className="nb-badge bg-white"><GraduationCap className="w-4 h-4" /> {top.education}</span>
              {top.bright_outlook && (
                <span data-testid="bright-outlook-top" className="nb-badge bg-brand-yellow"><Sun className="w-4 h-4" /> Bright Outlook</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {top.skills.map((s) => <span key={s} className="text-xs font-bold bg-black text-white rounded-full px-3 py-1">{s}</span>)}
            </div>
          </div>
          <button
            data-testid={`save-btn-${top.onet_code}`}
            onClick={() => toggleSave(top)}
            className="nb-btn bg-white flex items-center gap-2 self-start"
          >
            {saved.has(top.onet_code) ? <BookmarkCheck className="w-5 h-5 text-brand-primary" /> : <Bookmark className="w-5 h-5" />}
            {saved.has(top.onet_code) ? "Saved" : "Save"}
          </button>
        </div>
      </motion.div>

      {/* Other matches bento */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {rest.map((occ, i) => (
          <motion.div
            key={occ.onet_code}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            data-testid={`match-card-${occ.onet_code}`}
            className="nb-card p-5 flex flex-col"
          >
            <div className="flex items-start justify-between mb-2">
              <span className="font-heading text-3xl font-bold text-brand-primary">{occ.match_score}%</span>
              <button
                data-testid={`save-btn-${occ.onet_code}`}
                onClick={() => toggleSave(occ)}
                className="border-2 border-black rounded-lg p-1.5 shadow-nb-xs active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all bg-white"
              >
                {saved.has(occ.onet_code) ? <BookmarkCheck className="w-4 h-4 text-brand-primary" /> : <Bookmark className="w-4 h-4" />}
              </button>
            </div>
            <h3 className="font-heading text-lg font-bold text-black mb-1">{occ.title}</h3>
            {occ.interest_area && (
              <span className="nb-badge text-xs mb-2" style={{ backgroundColor: occ.interest_area.color }}>
                {occ.interest_area.title}
              </span>
            )}
            <p className="text-sm font-semibold text-slate-600 mb-3 flex-1">{occ.description}</p>
            <div className="flex flex-wrap gap-1.5">
              <span className="nb-badge bg-brand-yellow text-xs"><DollarSign className="w-3 h-3" />${(occ.salary / 1000).toFixed(0)}k</span>
              <span className="nb-badge bg-brand-purple text-xs"><GraduationCap className="w-3 h-3" />{occ.education.split(" ")[0]}</span>
              {occ.bright_outlook && (
                <span className="nb-badge bg-brand-yellow text-xs"><Sun className="w-3 h-3" /> Bright</span>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add your career -> real social proof */}
      <div data-testid="my-career-card" className="nb-card p-6 bg-brand-purple mb-10">
        <div className="flex items-center gap-2 mb-2">
          <Briefcase className="w-5 h-5" />
          <h2 className="font-heading text-xl md:text-2xl font-bold text-black">
            {careerSaved ? "Your career is helping others" : "Already have a career? Help the community"}
          </h2>
        </div>
        <p className="font-semibold text-black/70 mb-4">
          Share your current profession and how happy you are — future users with a taste like yours will see you in "People like you".
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-sm font-bold uppercase tracking-wider block mb-1">Your profession</label>
            <input
              data-testid="career-profession-input"
              className="nb-input"
              placeholder="e.g. UX Designer"
              value={career.profession}
              onChange={(e) => setCareer({ ...career, profession: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-bold uppercase tracking-wider block mb-1">Happiness</label>
            <div className="flex gap-1 bg-white border-2 border-black rounded-xl px-3 py-2.5 shadow-nb-xs">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`happiness-star-${n}`}
                  onClick={() => setCareer({ ...career, happiness: n })}
                  aria-label={`${n} stars`}
                >
                  <Star className={`w-6 h-6 ${n <= career.happiness ? "fill-brand-yellow text-black" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <button data-testid="save-career-btn" onClick={saveCareer} className="nb-btn bg-brand-primary text-white flex items-center gap-2 justify-center">
            {careerSaved ? <><Check className="w-4 h-4" /> Update</> : "Add me"}
          </button>
        </div>
      </div>

      {/* Social proof */}
      {data.social_proof?.length > 0 && (
        <section>
          <span className="nb-badge bg-brand-purple mb-3"><Users className="w-4 h-4" /> People like you</span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-black mb-1">Others with your taste</h2>
          <p className="font-semibold text-slate-600 mb-5">People who liked similar activities — and how happy they are in their careers.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.social_proof.map((p, i) => (
              <motion.div
                key={`${p.name}-${i}`}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                data-testid={`social-proof-${i}`}
                className="nb-card p-5 text-center"
              >
                <img src={p.avatar} alt={p.name} className="w-16 h-16 rounded-full border-2 border-black object-cover mx-auto mb-3 shadow-nb-xs" />
                <p className="font-heading font-bold text-black flex items-center justify-center gap-1">
                  {p.name}
                  {p.is_real && <BadgeCheck className="w-4 h-4 text-brand-secondary" title="Verified member" />}
                </p>
                <p className="text-sm font-bold text-brand-primary mb-2">{p.profession}</p>
                <div className="flex justify-center gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= p.happiness ? "fill-brand-yellow text-black" : "text-slate-300"}`} />
                  ))}
                </div>
                <span className="text-xs font-bold bg-brand-secondary border-2 border-black rounded-full px-2 py-0.5">{p.similarity}% match</span>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Recruiter modal */}
      <AnimatePresence>
        {showRecruiter && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowRecruiter(false)}
          >
            <motion.div
              data-testid="recruiter-modal"
              className="nb-card p-6 md:p-7 bg-white w-full max-w-md"
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-1">
                <h2 className="font-heading text-2xl font-bold text-black">Connect with a Recruiter</h2>
                <button data-testid="recruiter-close" onClick={() => setShowRecruiter(false)} className="border-2 border-black rounded-lg p-1 shadow-nb-xs bg-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="font-semibold text-slate-600 mb-4 text-sm">
                We'll send your top career matches and contact details to a recruiter who can help.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-1">Name</label>
                  <input data-testid="recruiter-name" className="nb-input" value={recruiter.name}
                    onChange={(e) => setRecruiter({ ...recruiter, name: e.target.value })} placeholder="Your name" />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-1">Email</label>
                  <input data-testid="recruiter-email" type="email" className="nb-input" value={recruiter.email}
                    onChange={(e) => setRecruiter({ ...recruiter, email: e.target.value })} placeholder="you@email.com" />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-1">Phone</label>
                  <input data-testid="recruiter-phone" className="nb-input" value={recruiter.phone}
                    onChange={(e) => setRecruiter({ ...recruiter, phone: e.target.value })} placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase tracking-wider block mb-1">Message</label>
                  <textarea data-testid="recruiter-message" rows={3} className="nb-input resize-none" value={recruiter.message}
                    onChange={(e) => setRecruiter({ ...recruiter, message: e.target.value })} placeholder="Anything you'd like the recruiter to know…" />
                </div>
              </div>
              <button
                data-testid="recruiter-submit"
                onClick={submitRecruiter}
                disabled={sending}
                className="nb-btn bg-brand-primary text-white w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {sending ? "Sending…" : <>Send to recruiter <Send className="w-4 h-4" /></>}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
